import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { useAccountRepository } from '@frontend/repository';
import { SettingKey } from '@frontend/setting/types';
import { TransactionPudoExtendedClient } from '@frontend/transaction/api';
import { ExtendedPudoTransaction, TransactionStateName } from '@frontend/transaction/types';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { useLicenseRepository } from '../../utils/license-repository';
import {
    BULK_MOVE_MAX_STALE_DAYS,
    BULK_MOVE_MIN_STALE_DAYS,
    clampStaleTransactionDays,
    filterStaleBulkMoveTransactions,
    resolveStaleTransactionDays
} from './bulk-move-age-filter';

export interface BulkMoveTransactionsListViewProps {
    transactions: ExtendedPudoTransaction[];
    selectedIds: Set<string>;
    selectedCount: number;
    staleDays: number;
    minStaleDays: number;
    maxStaleDays: number;
    setStaleDays: (days: number) => void;
    isLoading: boolean;
    error: ReactNode | null;
    canAct: boolean;
    canMove: boolean;
    canRemove: boolean;
    toggleSelection: (transactionId: string) => void;
    handleMove: () => void;
    handleRemove: () => void;
    handleBack: () => void;
}

const FETCH_SIZE = '100';

export const useBulkMoveTransactionsList = (): BulkMoveTransactionsListViewProps => {
    const navigate = useKioskNavigate();
    const { currentAccount } = useAccountRepository();
    const spotId = useAppSelector((store) => store.navigation.spot?.id);
    const { getFunctionality, getFunctionalitySetting } = useLicenseRepository();
    const bulkFunctionality = getFunctionality(Application.PUDO, Feature.BULK_MOVE_TRANSACTION);
    const bulkSettings = getFunctionalitySetting(bulkFunctionality?.id ?? '');
    const removePackageWorkflowId = bulkSettings?.[SettingKey.REMOVE_PACKAGE_WORKFLOW_ID] as string | undefined;
    const configuredStaleDays = resolveStaleTransactionDays(bulkSettings?.[SettingKey.STALE_TRANSACTION_DAYS]);

    const [rawTransactions, setRawTransactions] = useState<ExtendedPudoTransaction[]>([]);
    const [staleDays, setStaleDaysState] = useState(configuredStaleDays);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<ReactNode | null>(null);

    // Keep slider initial value in sync when settings load/change.
    useEffect(() => {
        setStaleDaysState(configuredStaleDays);
    }, [configuredStaleDays]);

    const transactions = useMemo(() => filterStaleBulkMoveTransactions(rawTransactions, new Date(), staleDays), [rawTransactions, staleDays]);

    useEffect(() => {
        setSelectedIds(new Set(transactions.map((tx) => tx.id)));
    }, [transactions]);

    const load = useCallback(async () => {
        if (!currentAccount || !spotId) {
            setRawTransactions([]);
            setIsLoading(false);
            setError(null);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const shared = {
                account_id: currentAccount,
                spot_id: spotId,
                application: Application.PUDO,
                index: '0',
                size: FETCH_SIZE
            };

            const [dropOff, pickUp] = await Promise.all([
                TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                    ...shared,
                    state: TransactionStateName.TRANSACTION_DROP_OFF_PENDING,
                    sort_field: 'creation_timestamp',
                    sort_direction: 'asc'
                }),
                TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                    ...shared,
                    state: TransactionStateName.TRANSACTION_PICK_UP_PENDING,
                    sort_field: 'dropped_off_timestamp',
                    sort_direction: 'asc'
                })
            ]);

            setRawTransactions([...(dropOff.results ?? []), ...(pickUp.results ?? [])]);
        } catch {
            setRawTransactions([]);
            setError(CommonMessage.ERRORS.FAILED_TO_LOAD);
        } finally {
            setIsLoading(false);
        }
    }, [currentAccount, spotId]);

    useEffect(() => {
        load();
    }, [load]);

    const setStaleDays = useCallback((days: number) => {
        setStaleDaysState(clampStaleTransactionDays(days));
    }, []);

    const toggleSelection = useCallback((transactionId: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(transactionId)) {
                next.delete(transactionId);
            } else {
                next.add(transactionId);
            }
            return next;
        });
    }, []);

    const selectedTransactions = useMemo(() => transactions.filter((tx) => selectedIds.has(tx.id)), [transactions, selectedIds]);

    const selectedHasPickUpPending = useMemo(
        () => selectedTransactions.some((tx) => tx.state === TransactionStateName.TRANSACTION_PICK_UP_PENDING),
        [selectedTransactions]
    );

    const canAct = selectedIds.size > 0 && !isLoading;
    // Pick-up pending remove and move both create a REMOVAL package, so both need the workflow.
    const canActWithRemoveWorkflow = canAct && (!selectedHasPickUpPending || !!removePackageWorkflowId);
    const canMove = canActWithRemoveWorkflow;
    const canRemove = canActWithRemoveWorkflow;

    const handleMove = useCallback(() => {
        if (!canMove) return;
        navigate('/spots/layout/pudo/bulk-move-transactions/move', {
            state: { transactions: selectedTransactions }
        });
    }, [canMove, navigate, selectedTransactions]);

    const handleRemove = useCallback(() => {
        if (!canRemove) return;
        navigate('/spots/layout/pudo/bulk-move-transactions/remove', {
            state: { transactions: selectedTransactions }
        });
    }, [canRemove, navigate, selectedTransactions]);

    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        transactions,
        selectedIds,
        selectedCount: selectedIds.size,
        staleDays,
        minStaleDays: BULK_MOVE_MIN_STALE_DAYS,
        maxStaleDays: BULK_MOVE_MAX_STALE_DAYS,
        setStaleDays,
        isLoading,
        error,
        canAct,
        canMove,
        canRemove,
        toggleSelection,
        handleMove,
        handleRemove,
        handleBack
    };
};
